import { useState } from "react";
import "@/App.css";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import * as XLSX from "xlsx";
import { Document, Packer, Paragraph, TextRun, HeadingLevel } from "docx";
import { saveAs } from "file-saver";
import { Loader2, Download, Sparkles, Cpu, Briefcase, TrendingUp, FileText, Copy, Edit3, Check } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * 8 role archetypes used on the Generate page. Each maps a "how the role works"
 * profile to a behavioural / technical weighting that we send to the AI prompt.
 * Sub-labels are intentionally industry-neutral — RoleIQ serves every industry.
 */
const ROLE_ARCHETYPES = [
  { key: "leadership", title: "Leadership & People", sub: "Anyone who manages or develops others", weighting: "80% behavioural / 20% technical", behavioural: 80 },
  { key: "technical", title: "Technical Specialist", sub: "Deep expertise roles — any craft, any field", weighting: "20% behavioural / 80% technical", behavioural: 20 },
  { key: "blended", title: "Balanced Generalist", sub: "Roles mixing coordination and expertise", weighting: "50% behavioural / 50% technical", behavioural: 50 },
  { key: "customer_facing", title: "Customer-Facing", sub: "Roles where client interaction drives success", weighting: "60% behavioural / 40% technical", behavioural: 60 },
  { key: "operational", title: "Operational Excellence", sub: "Process, quality, and execution roles", weighting: "35% behavioural / 65% technical", behavioural: 35 },
  { key: "strategic", title: "Strategic & Executive", sub: "Direction-setting and enterprise roles", weighting: "70% behavioural / 30% technical", behavioural: 70 },
  { key: "compliance", title: "Compliance & Precision", sub: "Accuracy-critical and regulated roles", weighting: "30% behavioural / 70% technical", behavioural: 30 },
  { key: "custom", title: "Custom", sub: "Set your own mix for anything else", weighting: "You decide — opens slider", behavioural: null },
];

function archetypeFor(key) {
  return ROLE_ARCHETYPES.find((a) => a.key === key) || ROLE_ARCHETYPES[2];
}

function GeneratePage() {
  const { user, refreshUser, decrementTokens } = useAuth();
  const [formData, setFormData] = useState({
    company_name: "",
    industry: "",
    industry_other: "",
    company_values: "",
    job_role: "",
    department: "",
    role_level: "",
    existing_jd: "",
    primary_function: "",
    primary_function_other: "",
    key_responsibilities: "",
    organisation_size: "",
    organisation_size_context: "",
    key_challenges: "",
    competency_focus: "blended",
    competency_focus_context: "",
    custom_behavioural_weight: 50,
    seniority_context: "",
    seniority_context_detail: "",
  });

  const [useExistingJD, setUseExistingJD] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [generatedJD, setGeneratedJD] = useState(null);
  const [loadingJD, setLoadingJD] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showSignupGate, setShowSignupGate] = useState(false);
  const [limitReached, setLimitReached] = useState(false);

  // Prefill from "Generate JD from this map" flow
  if (typeof window !== "undefined") {
    const prefill = sessionStorage.getItem("prefill_map");
    if (prefill) {
      sessionStorage.removeItem("prefill_map");
      try {
        const data = JSON.parse(prefill);
        // Use a one-shot effect via setState during render — guard with a marker
        if (!formData._prefilled) {
          setFormData((prev) => ({ ...prev, ...data, _prefilled: true }));
        }
      } catch (err) { console.error(err); }
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    // Only check free trial for anonymous (not-logged-in) users
    if (!user && localStorage.getItem("roleiq_free_used") === "true") {
      setShowSignupGate(true);
      return;
    }
    // If user is logged in, clear any old free trial flag
    if (user) {
      localStorage.removeItem("roleiq_free_used");
    }
    setLoading(true);
    setGeneratedJD(null);
    setShowSignupGate(false);
    setLimitReached(false);

    try {
      const config = user ? { withCredentials: true } : {};
      // Derive the behavioural weighting (preset → archetype value; custom → slider).
      const archetype = archetypeFor(formData.competency_focus);
      const behaviouralWeight = archetype.behavioural === null ? formData.custom_behavioural_weight : archetype.behavioural;
      const technicalWeight = 100 - behaviouralWeight;
      const response = await axios.post(`${API}/generate-competency`, {
        company_name: formData.company_name,
        industry: formData.industry,
        industry_other: formData.industry_other || "",
        company_values: formData.company_values,
        job_role: formData.job_role,
        department: formData.department,
        role_level: formData.role_level,
        custom_title: formData.custom_title || "",
        existing_jd: useExistingJD ? formData.existing_jd : null,
        primary_function: formData.primary_function || "",
        primary_function_other: formData.primary_function_other || "",
        key_responsibilities: formData.key_responsibilities || "",
        organisation_size: formData.organisation_size || "",
        organisation_size_context: formData.organisation_size_context || "",
        key_challenges: formData.key_challenges || "",
        competency_focus: formData.competency_focus || "blended",
        competency_focus_context: formData.competency_focus_context || "",
        behavioural_weight: behaviouralWeight,
        technical_weight: technicalWeight,
        seniority_context: formData.seniority_context || "",
        seniority_context_detail: formData.seniority_context_detail || "",
      }, config);
      setResults(response.data);
      // Decrement competency token in context for instant Navbar update
      if (user) {
        decrementTokens("competency", 1);
      }
      if (response.data.values_capped && response.data.message) {
        alert(response.data.message);
      }
      // Mark free trial used for anonymous users
      if (!user) {
        localStorage.setItem("roleiq_free_used", "true");
      }
      // Auto-save map if user is logged in
      if (user) {
        try {
          await axios.post(`${BACKEND_URL}/api/maps`, {
            company_name: formData.company_name,
            industry: formData.industry,
            job_role: formData.job_role,
            department: formData.department,
            role_level: formData.role_level,
            company_values: formData.company_values,
            primary_function: formData.primary_function || "",
            primary_function_other: formData.primary_function_other || "",
            industry_other: formData.industry_other || "",
            key_responsibilities: formData.key_responsibilities || "",
            organisation_size: formData.organisation_size || "",
            organisation_size_context: formData.organisation_size_context || "",
            key_challenges: formData.key_challenges || "",
            competency_focus: formData.competency_focus || "blended",
            competency_focus_context: formData.competency_focus_context || "",
            behavioural_weight: behaviouralWeight,
            technical_weight: technicalWeight,
            seniority_context: formData.seniority_context || "",
            seniority_context_detail: formData.seniority_context_detail || "",
            results: response.data,
          }, { withCredentials: true });
        } catch (e) {
          console.log("Auto-save skipped:", e.message);
        }
      }
    } catch (error) {
      console.error("Error generating competency:", error);
      const detail = error?.response?.data?.detail || "";
      if (detail === "FREE_TRIAL_USED") {
        localStorage.setItem("roleiq_free_used", "true");
        setShowSignupGate(true);
      } else if (detail === "NO_TOKENS" || detail === "PLAN_LIMIT_REACHED") {
        setLimitReached(true);
      } else {
        alert(detail || "Error generating competency map. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateJD = async () => {
    if (!results) return;
    
    setLoadingJD(true);
    try {
      const response = await axios.post(`${API}/generate-jd`, {
        company_name: formData.company_name,
        industry: formData.industry,
        company_values: formData.company_values,
        job_role: formData.job_role,
        department: formData.department,
        role_level: formData.role_level,
        role_summary: results.role_summary,
        competency_domains: results.competency_domains,
      });
      setGeneratedJD(response.data.job_description);
      // Auto-save JD if user is logged in
      if (user) {
        try {
          await axios.post(`${BACKEND_URL}/api/jds`, {
            company_name: formData.company_name,
            industry: formData.industry,
            job_role: formData.job_role,
            department: formData.department,
            role_level: formData.role_level,
            job_description: response.data.job_description,
          }, { withCredentials: true });
        } catch (e) {
          console.log("JD auto-save skipped:", e.message);
        }
      }
    } catch (error) {
      console.error("Error generating JD:", error);
      const msg = error?.response?.data?.detail || "Error generating Job Description. Please try again.";
      alert(msg);
    } finally {
      setLoadingJD(false);
    }
  };

  const handleCopyJD = () => {
    if (!generatedJD) return;
    const cleanedJD = generatedJD.replace(/\[EDITABLE\]/g, '___________');
    navigator.clipboard.writeText(cleanedJD);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadJDWord = async () => {
    if (!generatedJD) return;

    try {
      const lines = generatedJD.split('\n').filter(line => line.trim());
      
      const children = lines.map((line) => {
        const trimmedLine = line.trim();
        
        if (trimmedLine.includes('— JOB DESCRIPTION')) {
          return new Paragraph({
            text: trimmedLine,
            heading: HeadingLevel.HEADING_1,
            spacing: { after: 300 },
          });
        }
        
        const isEditable = trimmedLine.includes('[EDITABLE]');
        const displayText = trimmedLine.replace(/\[EDITABLE\]/g, '___________');
        
        return new Paragraph({
          children: [
            new TextRun({
              text: displayText,
              highlight: isEditable ? 'yellow' : undefined,
            }),
          ],
          spacing: { after: 100 },
        });
      });

      const doc = new Document({
        sections: [{ properties: {}, children: children }],
      });

      const blob = await Packer.toBlob(doc);
      saveAs(blob, `${formData.company_name.replace(/\s+/g, '_')}_${formData.job_role.replace(/\s+/g, '_')}_JD.docx`);
    } catch (error) {
      console.error("Error generating Word document:", error);
      alert("Error downloading Word document.");
    }
  };

  const handleDownloadExcel = () => {
    if (!results) return;

    const wb = XLSX.utils.book_new();

    // Sheet 1: Instructions
    const instructionsData = [
      [`${formData.company_name} Competency Framework - Designed & Powered by PCC Tejinder Singh Sehdev`],
      [""],
      ["THREE-TIER COMPETENCY ARCHITECTURE:"],
      ["LEVEL 1: Company Value / Core Competency Domain"],
      ["LEVEL 2: Role Competency / Main Skills (Skill + Knowledge + Ability)"],
      ["LEVEL 3: Observable Sub-Skills (Measurable actions)"],
      [""],
      ["HOW TO USE:"],
      ["1. Each company value maps to 2-3 Role Competencies"],
      ["2. Each Role Competency has a definition, KPIs, and observable skills"],
      ["3. Use for hiring, performance reviews, and talent development"],
      [""],
      ["COLOR LEGEND:"],
      ["🟡 GOLD = KPIs (add your target numbers)"],
      ["🔵 BLUE = Blank rows for custom skills"],
      [""],
      ["ROLE SUMMARY:"],
      [results.role_summary],
      [""],
      [`Generated for: ${formData.job_role} - ${formData.role_level}`],
      [`Company: ${formData.company_name} | Industry: ${formData.industry}`],
      [""],
      ["DISCLAIMER:"],
      ["Note: AI-generated content may contain inaccuracies. Please review all competencies with a qualified HR professional before formal use in hiring, performance management, or development processes. RoleIQ is a decision-support tool — not a substitute for professional HR judgement."],
    ];

    const ws1 = XLSX.utils.aoa_to_sheet(instructionsData);
    XLSX.utils.book_append_sheet(wb, ws1, "Instructions");

    // Sheet 2: Competency Map (3-tier structure)
    const competencyData = [
      [
        "Company Value / Core Competency (L1)",
        "Role Competency / Main Skill (L2)",
        "Definition",
        "KPI / Observable Sub-Skill (L3)",
        "Fits Current Role?",
        "Fits Future Role?",
        "Priority",
        "Leader Notes",
      ],
    ];

    // Loop through ALL company values/domains
    results.competency_domains.forEach((domain, domainIdx) => {
      // Add domain header row
      competencyData.push([
        `${formData.company_name.toUpperCase()} CORE COMPETENCY — ${domain.company_value.toUpperCase()}`,
        "",
        "",
        "",
        "",
        "",
        "",
        "",
      ]);

      // Loop through role competencies for this domain
      domain.role_competencies.forEach((roleComp, rcIdx) => {
        // Add Role Competency header row
        competencyData.push([
          "",
          roleComp.name,
          roleComp.definition,
          "",
          "",
          "",
          "",
          "",
        ]);

        // Add KPIs
        roleComp.kpis.forEach((kpi, kpiIdx) => {
          competencyData.push([
            "",
            "",
            "",
            `🟡 KPI ${kpiIdx + 1}: ${kpi.description}`,
            "",
            "",
            "",
            "",
          ]);
        });

        // Add Observable Skills
        roleComp.observable_skills.forEach((skill, skillIdx) => {
          competencyData.push([
            "",
            "",
            "",
            `${skillIdx + 1}. ${skill.text}`,
            "",
            "",
            "",
            "",
          ]);
        });

        // Add blank row between role competencies
        if (rcIdx < domain.role_competencies.length - 1) {
          competencyData.push(["", "", "", "", "", "", "", ""]);
        }
      });

      // Add AI-Enhanced skill INSIDE this domain section (after all role competencies)
      competencyData.push([
        "",
        "🟢 AI-Enhanced Capability",
        `How AI amplifies ${domain.company_value}`,
        domain.ai_enhanced_skill,
        "",
        "",
        "",
        "",
      ]);

      // Add blank rows for custom skills (for this domain)
      for (let i = 0; i < 5; i++) {
        competencyData.push([
          "",
          "",
          "",
          `🔵 Add custom skill for ${domain.company_value} here`,
          "",
          "",
          "",
          "",
        ]);
      }

      // Add separator between domains (only if not the last domain)
      if (domainIdx < results.competency_domains.length - 1) {
        competencyData.push(["", "", "", "", "", "", "", ""]);
        competencyData.push(["---", "---", "---", "---", "---", "---", "---", "---"]);
        competencyData.push(["", "", "", "", "", "", "", ""]);
      }
    });

    const ws2 = XLSX.utils.aoa_to_sheet(competencyData);
    ws2['!cols'] = [
      { wch: 30 }, { wch: 30 }, { wch: 40 }, { wch: 50 },
      { wch: 18 }, { wch: 18 }, { wch: 12 }, { wch: 30 },
    ];

    XLSX.utils.book_append_sheet(wb, ws2, "Competency Framework");

    const fileName = `${formData.company_name.replace(/\s+/g, "_")}_${formData.job_role.replace(/\s+/g, "_")}_Competency_Framework.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  return (
    <div className="App min-h-screen pt-16">
      <div className="flex flex-col lg:flex-row min-h-screen">
        {/* Left Sidebar */}
        <div className="form-sidebar w-full lg:w-[380px] lg:min-w-[380px] lg:sticky lg:top-16 lg:h-[calc(100vh-4rem)] overflow-y-auto lg:flex-shrink-0">
          <div className="relative z-10 p-8 lg:p-10">
            <div className="mb-8">
              <div className="job-role-icon">
                <Briefcase className="w-8 h-8 text-[#0F1E35]" />
              </div>
              <h1 className="text-4xl font-bold tracking-tight text-white playfair" data-testid="app-logo">
                RoleIQ
              </h1>
              <p className="text-white/70 text-sm mt-2 font-light">
                AI-Powered Competency Framework Builder
              </p>
            </div>

            <form onSubmit={handleGenerate} className="space-y-5">
              <div>
                <label className="gold-label">Company Name</label>
                <input
                  type="text"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleInputChange}
                  required
                  data-testid="input-company-name"
                  className="white-input"
                  placeholder="e.g., TechCorp Industries"
                />
              </div>

              <div>
                <label className="gold-label">Industry</label>
                <select
                  name="industry"
                  value={formData.industry}
                  onChange={handleInputChange}
                  required
                  data-testid="input-industry"
                  className="white-input"
                >
                  <option value="">Select industry</option>
                  <option>Technology</option>
                  <option>Manufacturing</option>
                  <option>Automotive</option>
                  <option>Banking and Financial Services</option>
                  <option>Healthcare and Pharmaceuticals</option>
                  <option>Retail and E-commerce</option>
                  <option>FMCG / Consumer Goods</option>
                  <option>Telecom</option>
                  <option>Energy and Utilities</option>
                  <option>Media and Entertainment</option>
                  <option>Logistics and Supply Chain</option>
                  <option>Education</option>
                  <option>Real Estate and Construction</option>
                  <option>Hospitality and Travel</option>
                  <option>Professional Services / Consulting</option>
                  <option>Government / Public Sector</option>
                  <option>Other</option>
                </select>
                <AnimatePresence>
                  {formData.industry === "Other" && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.25 }}>
                      <input
                        type="text"
                        name="industry_other"
                        value={formData.industry_other}
                        onChange={handleInputChange}
                        data-testid="input-industry-other"
                        className="white-input mt-2"
                        placeholder="Please specify your industry — e.g., Aerospace, Mining, Social Sector, NGO"
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <div>
                <label className="gold-label">Company Core Competencies</label>
                <textarea
                  name="company_values"
                  value={formData.company_values}
                  onChange={handleInputChange}
                  data-testid="input-company-values"
                  rows="3"
                  className="white-input resize-none"
                  placeholder="Enter values separated by commas. Leave blank for AI suggestions.&#10;&#10;Example: Innovation, Quality, Customer Focus"
                />
                <p className="text-white/50 text-xs mt-2">
                  💡 Optional: AI will suggest competencies if blank
                </p>
              </div>

              <div>
                <label className="gold-label">Job Role</label>
                <input
                  type="text"
                  name="job_role"
                  value={formData.job_role}
                  onChange={handleInputChange}
                  required
                  data-testid="input-job-role"
                  className="white-input"
                  placeholder="e.g., Senior Product Manager"
                />
              </div>

              <div>
                <label className="gold-label">Department</label>
                <input
                  type="text"
                  name="department"
                  value={formData.department}
                  onChange={handleInputChange}
                  required
                  data-testid="input-department"
                  className="white-input"
                  placeholder="e.g., Product Development"
                />
              </div>

              <div className="pt-4 border-t border-white/10">
                <label className="gold-label mb-3 block">Add more detail for a more accurate framework — optional but recommended</label>

                <div className="space-y-4">
                  <div>
                    <label className="text-white/80 text-xs font-semibold mb-1 block">Primary Function</label>
                    <select
                      name="primary_function"
                      value={formData.primary_function}
                      onChange={handleInputChange}
                      data-testid="input-primary-function"
                      className="white-input"
                    >
                      <option value="">Select function (optional)</option>
                      <option>Human Resources</option>
                      <option>Learning and Development</option>
                      <option>Talent Management</option>
                      <option>Sales</option>
                      <option>Marketing</option>
                      <option>Finance</option>
                      <option>Operations</option>
                      <option>Supply Chain</option>
                      <option>Manufacturing</option>
                      <option>Technology</option>
                      <option>Customer Service</option>
                      <option>Legal and Compliance</option>
                      <option>Strategy</option>
                      <option>General Management</option>
                      <option>Other</option>
                    </select>
                    <AnimatePresence>
                      {formData.primary_function === "Other" && (
                        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.25 }}>
                          <input
                            type="text"
                            name="primary_function_other"
                            value={formData.primary_function_other}
                            onChange={handleInputChange}
                            data-testid="input-primary-function-other"
                            className="white-input mt-2"
                            placeholder="Please specify your function — e.g., Corporate Affairs, ESG, Research and Development"
                          />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  <div>
                    <label className="text-white/80 text-xs font-semibold mb-1 block">Key Responsibilities</label>
                    <p className="text-white/50 text-[11px] mb-1">What does this person actually do day to day?</p>
                    <textarea
                      name="key_responsibilities"
                      value={formData.key_responsibilities}
                      onChange={handleInputChange}
                      maxLength={1500}
                      data-testid="input-key-responsibilities"
                      rows="3"
                      className="white-input resize-none"
                      placeholder="Example: Manages talent development programmes for 5000 employees across manufacturing plants, designs leadership academies, runs TNI and succession planning."
                    />
                    <p className="text-white/40 text-[10px] mt-1">Max 150 words</p>
                  </div>

                  <div>
                    <label className="text-white/80 text-xs font-semibold mb-1 block">Organisation Size</label>
                    <select
                      name="organisation_size"
                      value={formData.organisation_size}
                      onChange={handleInputChange}
                      data-testid="input-organisation-size"
                      className="white-input"
                    >
                      <option value="">Select size (optional)</option>
                      <option>Startup under 50 employees</option>
                      <option>Small 50 to 200</option>
                      <option>Mid-size 200 to 1000</option>
                      <option>Large 1000 to 5000</option>
                      <option>Enterprise 5000 plus</option>
                    </select>
                    <AnimatePresence>
                      {formData.organisation_size && (
                        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.25 }}>
                          <input
                            type="text"
                            name="organisation_size_context"
                            value={formData.organisation_size_context}
                            onChange={handleInputChange}
                            data-testid="input-organisation-size-context"
                            className="white-input mt-2"
                            placeholder="Add context — e.g., Family-owned business, Government PSU, Multinational subsidiary"
                          />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  <div>
                    <label className="gold-label">Key Challenges</label>
                    <p className="text-white/50 text-[11px] mb-1">What are the biggest challenges this role needs to handle?</p>
                    <textarea
                      name="key_challenges"
                      value={formData.key_challenges}
                      onChange={handleInputChange}
                      maxLength={1000}
                      data-testid="input-key-challenges"
                      rows="3"
                      className="white-input resize-none"
                      placeholder="e.g. Managing budgets under pressure, aligning L&D to business outcomes"
                    />
                    <p className="text-white/40 text-[10px] mt-1">Max 100 words</p>
                  </div>

                  {/* Competency Focus — 8 archetype cards */}
                  <div>
                    <label className="gold-label">Competency Focus</label>
                    <p className="text-white/50 text-[11px] mb-3">Pick the archetype that best matches how this role works. Weighting is sent to the AI.</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="competency-focus-group">
                      {ROLE_ARCHETYPES.map((arch) => {
                        const active = formData.competency_focus === arch.key;
                        return (
                          <button
                            key={arch.key}
                            type="button"
                            onClick={() => setFormData((prev) => ({ ...prev, competency_focus: arch.key }))}
                            data-testid={`competency-focus-${arch.key}`}
                            style={{
                              padding: "12px 14px",
                              borderRadius: "8px",
                              textAlign: "left",
                              border: active ? "1px solid #C9A84C" : "1px solid rgba(255,255,255,0.15)",
                              background: active ? "#C9A84C" : "rgba(255,255,255,0.04)",
                              color: active ? "#0F1E35" : "#ffffff",
                              transition: "all 0.18s ease",
                              cursor: "pointer",
                            }}
                          >
                            <p style={{ fontSize: "13px", fontWeight: 500, lineHeight: 1.3 }}>{arch.title}</p>
                            <p style={{ fontSize: "11px", marginTop: "4px", opacity: active ? 0.75 : 0.55, lineHeight: 1.35 }}>{arch.sub}</p>
                            <p style={{ fontSize: "11px", marginTop: "4px", fontWeight: 500, opacity: active ? 0.85 : 0.7 }}>{arch.weighting}</p>
                          </button>
                        );
                      })}
                    </div>

                    {/* Custom slider — only when "custom" is selected */}
                    <AnimatePresence>
                      {formData.competency_focus === "custom" && (
                        <motion.div
                          initial={{ opacity: 0, height: 0, marginTop: 0 }}
                          animate={{ opacity: 1, height: "auto", marginTop: 12 }}
                          exit={{ opacity: 0, height: 0, marginTop: 0 }}
                          transition={{ duration: 0.22 }}
                          data-testid="custom-weight-slider-panel"
                          style={{
                            background: "rgba(255,255,255,0.05)",
                            border: "0.5px solid rgba(201,168,76,0.3)",
                            borderRadius: "8px",
                            padding: "14px 16px",
                          }}
                        >
                          <div style={{ display: "flex", justifyContent: "space-between", fontSize: "11px", color: "rgba(255,255,255,0.55)", marginBottom: "6px" }}>
                            <span>Behavioural</span>
                            <span>Technical</span>
                          </div>
                          <input
                            type="range"
                            min={10}
                            max={90}
                            step={10}
                            value={formData.custom_behavioural_weight}
                            onChange={(e) => setFormData((prev) => ({ ...prev, custom_behavioural_weight: parseInt(e.target.value, 10) }))}
                            data-testid="custom-weight-slider"
                            style={{ width: "100%", accentColor: "#C9A84C" }}
                          />
                          <p style={{ textAlign: "center", marginTop: "8px", fontSize: "13px", color: "#C9A84C", fontWeight: 500 }} data-testid="custom-weight-readout">
                            {formData.custom_behavioural_weight}% behavioural / {100 - formData.custom_behavioural_weight}% technical
                          </p>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    <AnimatePresence>
                      {formData.competency_focus && formData.competency_focus !== "custom" && (
                        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.25 }}>
                          <input
                            type="text"
                            name="competency_focus_context"
                            value={formData.competency_focus_context}
                            onChange={handleInputChange}
                            data-testid="input-competency-focus-context"
                            className="white-input mt-2"
                            placeholder="e.g. Stakeholder management, data analysis"
                          />
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {/* Live weighting summary row */}
                    {formData.competency_focus && (
                      <div
                        style={{
                          marginTop: "14px",
                          paddingTop: "12px",
                          borderTop: "0.5px solid rgba(201,168,76,0.2)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: "12px",
                          flexWrap: "wrap",
                        }}
                        data-testid="weighting-summary-row"
                      >
                        <span style={{ fontSize: "12px", color: "rgba(255,255,255,0.5)" }}>Selected weighting</span>
                        <span style={{ fontSize: "13px", color: "#F2DC8A", fontWeight: 500 }} data-testid="weighting-summary-value">
                          {(() => {
                            const arch = archetypeFor(formData.competency_focus);
                            const beh = arch.behavioural === null ? formData.custom_behavioural_weight : arch.behavioural;
                            return `${beh}% behavioural / ${100 - beh}% technical`;
                          })()}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Seniority Context */}
                  <div>
                    <label className="gold-label">Seniority Context</label>
                    <select
                      name="seniority_context"
                      value={formData.seniority_context}
                      onChange={handleInputChange}
                      data-testid="input-seniority-context"
                      className="white-input"
                    >
                      <option value="">Select seniority context (optional)</option>
                      <option>Individual Contributor</option>
                      <option>Team Lead</option>
                      <option>Manager</option>
                      <option>Senior Manager</option>
                      <option>Director and Above</option>
                    </select>
                    <AnimatePresence>
                      {formData.seniority_context && (
                        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.25 }}>
                          <input
                            type="text"
                            name="seniority_context_detail"
                            value={formData.seniority_context_detail}
                            onChange={handleInputChange}
                            data-testid="input-seniority-context-detail"
                            className="white-input mt-2"
                            placeholder="Add role-specific context — e.g., First time in this level, transitioning from technical to managerial, leading a newly formed team"
                          />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              </div>

              <div>
                <label className="gold-label">Role Level</label>
                <select
                  name="role_level"
                  value={formData.role_level}
                  onChange={handleInputChange}
                  required
                  data-testid="select-role-level"
                  className="white-input"
                >
                  <option value="">Select Level</option>
                  <optgroup label="Standard Levels">
                    <option value="Intern / Trainee">Intern / Trainee (0-1 year)</option>
                    <option value="Entry Level / Junior">Entry Level / Junior (1-3 years)</option>
                    <option value="Mid Level / Individual Contributor">Mid Level / Individual Contributor (3-6 years)</option>
                    <option value="Senior Level / Senior IC">Senior Level / Senior IC (6-10 years)</option>
                    <option value="Lead / Team Lead">Lead / Team Lead (5-8 years)</option>
                    <option value="Assistant Manager / Deputy Manager">Assistant Manager / Deputy Manager</option>
                    <option value="Manager">Manager</option>
                    <option value="Senior Manager">Senior Manager</option>
                    <option value="Associate Director / Deputy Director">Associate Director / Deputy Director</option>
                    <option value="Director">Director</option>
                    <option value="Senior Director / Group Director">Senior Director / Group Director</option>
                    <option value="Vice President (VP)">Vice President (VP)</option>
                    <option value="Senior Vice President (SVP)">Senior Vice President (SVP)</option>
                    <option value="Executive Vice President (EVP)">Executive Vice President (EVP)</option>
                    <option value="General Manager (GM)">General Manager (GM)</option>
                    <option value="Chief Officer (CXO)">Chief [Function] Officer (CFO/CHRO/CMO/CTO)</option>
                    <option value="CEO / MD">Chief Executive Officer (CEO)</option>
                    <option value="CEO / MD">Managing Director (MD)</option>
                    <option value="Board Member / Trustee">Board Member / Trustee</option>
                  </optgroup>
                </select>
              </div>

              <div>
                <label className="gold-label">Or type your exact role/title (optional)</label>
                <input
                  type="text"
                  name="custom_title"
                  value={formData.custom_title || ""}
                  onChange={handleInputChange}
                  data-testid="input-custom-title"
                  className="white-input"
                  placeholder='e.g., Regional Sales Head, Territory Manager'
                />
                <p className="text-white/50 text-xs mt-1">When filled, AI uses this title in the output</p>
              </div>

              <div className="pt-4 border-t border-white/10">
                <div className="flex items-center justify-between mb-3">
                  <label className="gold-label mb-0">Have an existing Job Description?</label>
                  <button
                    type="button"
                    onClick={() => setUseExistingJD(!useExistingJD)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      useExistingJD ? 'bg-[#C9A84C]' : 'bg-white/20'
                    }`}
                    data-testid="jd-toggle"
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        useExistingJD ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
                
                {useExistingJD && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                  >
                    <textarea
                      name="existing_jd"
                      value={formData.existing_jd}
                      onChange={handleInputChange}
                      data-testid="input-existing-jd"
                      rows="8"
                      className="white-input resize-none"
                      placeholder="Paste your existing Job Description here. AI will extract and map competencies."
                    />
                    <p className="text-white/50 text-xs mt-2">
                      ✨ AI will use this as primary source
                    </p>
                  </motion.div>
                )}
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="generate-button"
                className="generate-button flex items-center justify-center gap-2 mt-6"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 loader" />
                    Generating Competency Framework...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5" />
                    Generate Competency Framework
                  </>
                )}
              </button>
            </form>

            <p className="text-white/40 text-xs mt-8 text-center leading-relaxed">
              Designed & Powered by<br />
              <span className="text-[#C9A84C]/70 font-semibold">PCC Tejinder Singh Sehdev</span>
            </p>
          </div>
        </div>

        {/* Right Pane - Results */}
        <div className="w-full lg:flex-1 bg-white p-8 lg:p-12 min-h-screen" data-testid="results-pane">
          {/* Signup Gate after free trial */}
          {showSignupGate && (
            <div className="flex items-center justify-center h-full">
              <div className="text-center max-w-md bg-cream rounded-2xl p-10">
                <Sparkles className="w-12 h-12 text-[#C9A84C] mx-auto mb-4" />
                <h2 className="text-2xl text-[#0F1E35] mb-3 playfair font-bold">You&apos;ve used your 2 free maps!</h2>
                <p className="text-slate-600 mb-6 leading-relaxed">
                  Create a free account to get <strong>5 more competency maps</strong>, save your work, and join the HR community.
                </p>
                <a href="/signup" className="inline-block bg-[#C9A84C] text-[#0F1E35] font-bold px-8 py-3 rounded-lg hover:bg-[#b59540] transition-colors" data-testid="signup-gate-btn">
                  Create Free Account
                </a>
                <p className="text-slate-400 text-xs mt-4">Already have an account? <a href="/login" className="text-[#C9A84C] hover:underline">Login</a></p>
              </div>
            </div>
          )}

          {/* Plan limit reached */}
          {limitReached && (
            <div className="flex items-center justify-center h-full">
              <div className="text-center max-w-md bg-cream rounded-2xl p-10">
                <TrendingUp className="w-12 h-12 text-[#C9A84C] mx-auto mb-4" />
                <h2 className="text-2xl text-[#0F1E35] mb-3 playfair font-bold">You&apos;ve used all your tokens</h2>
                <p className="text-slate-600 mb-6 leading-relaxed">
                  Purchase more tokens to continue generating competency maps. Tokens never expire.
                </p>
                <a href="/pricing" className="inline-block bg-[#C9A84C] text-[#0F1E35] font-bold px-8 py-3 rounded-lg hover:bg-[#b59540] transition-colors" data-testid="upgrade-btn">
                  Buy Tokens
                </a>
              </div>
            </div>
          )}

          {!results && !loading && !showSignupGate && !limitReached && (
            <div className="flex items-center justify-center h-full" data-testid="generate-empty-state">
              <div className="text-center max-w-md">
                <h2 className="text-2xl text-[#0F1E35] mb-3" style={{ fontFamily: "Georgia,'Times New Roman',serif", fontWeight: 600 }}>
                  Ready to Build Your Competency Framework
                </h2>
                <p className="text-slate-600 leading-relaxed mb-8">
                  A three-tier framework aligned with global standards — generated in 60 seconds
                </p>
                <ol className="text-left space-y-3 max-w-sm mx-auto">
                  {[
                    { n: 1, label: "Describe the role", Icon: Briefcase },
                    { n: 2, label: "Choose your focus", Icon: Cpu },
                    { n: 3, label: "Generate and refine", Icon: Sparkles },
                  ].map(({ n, label, Icon }) => (
                    <li key={n} className="flex items-center gap-3" data-testid={`empty-state-step-${n}`}>
                      <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#0F1E35] text-[#C9A84C] font-bold text-xs flex items-center justify-center">{n}</span>
                      <Icon className="w-4 h-4 text-[#C9A84C]" />
                      <span className="text-slate-700 text-sm">{label}</span>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          )}

          {loading && (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <Loader2 className="w-16 h-16 text-[#C9A84C] mx-auto mb-4 loader" />
                <h2 className="text-2xl text-[#0F1E35] mb-2 playfair font-semibold">
                  Generating Your Competency Framework
                </h2>
                <p className="text-slate-600 mb-2">
                  This takes 20-30 seconds...
                </p>
                <p className="text-slate-500 text-sm">
                  AI is analysing your role and building the competency framework
                </p>
              </div>
            </div>
          )}

          {results && (
            <AnimatePresence>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                {/* Action Buttons */}
                <div className="flex flex-wrap gap-3 justify-end mb-8">
                  <button
                    onClick={handleDownloadExcel}
                    data-testid="download-excel-button"
                    className="download-button"
                  >
                    <Download className="w-5 h-5" />
                    Download Excel
                  </button>
                  
                  <button
                    onClick={handleGenerateJD}
                    disabled={loadingJD}
                    data-testid="generate-jd-button"
                    className="bg-[#C9A84C] text-[#0F1E35] border-2 border-[#C9A84C] px-6 py-3 rounded-lg font-semibold text-sm transition-all flex items-center gap-2 hover:bg-[#b59540] disabled:opacity-50"
                  >
                    {loadingJD ? (
                      <>
                        <Loader2 className="w-5 h-5 loader" />
                        Generating JD...
                      </>
                    ) : (
                      <>
                        <FileText className="w-5 h-5" />
                        Generate Job Description
                      </>
                    )}
                  </button>
                </div>

                {/* Role Summary */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="role-summary-card"
                  data-testid="role-summary"
                >
                  <h2 className="role-summary-title">Role Summary</h2>
                  <p className="role-summary-text">{results.role_summary}</p>
                </motion.div>

                {/* 3-TIER Competency Structure */}
                <div className="space-y-8">
                  {results.competency_domains && results.competency_domains.map((domain, domainIdx) => (
                    <div key={domainIdx}>
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 + domainIdx * 0.1 }}
                        className="competency-card"
                        data-testid={`domain-card-${domainIdx}`}
                      >
                        {/* LEVEL 1: Navy Header - Company Value */}
                        <div className="competency-header">
                          <h3 className="competency-title">
                            {formData.company_name.toUpperCase()} CORE COMPETENCY — {domain.company_value.toUpperCase()}
                          </h3>
                        </div>

                        <div className="p-6 space-y-8">
                          {/* LEVEL 2: Role Competencies */}
                          {domain.role_competencies.map((roleComp, rcIdx) => (
                            <div key={rcIdx} className="role-competency-section">
                              {/* Gold Subheader */}
                              <h4 className="role-competency-name">
                                {roleComp.name}
                              </h4>
                              
                              {/* Definition */}
                              <p className="competency-definition">
                                {roleComp.definition}
                              </p>

                              {/* KPIs */}
                              <div className="mb-4">
                                <h5 className="section-heading-sm">
                                  Key Performance Indicators
                                </h5>
                                <div className="flex flex-wrap gap-2">
                                  {roleComp.kpis.map((kpi, kpiIdx) => (
                                    <div
                                      key={kpiIdx}
                                      className="kpi-tag"
                                      data-testid={`kpi-${domainIdx}-${rcIdx}-${kpiIdx}`}
                                    >
                                      {kpi.description}
                                    </div>
                                  ))}
                                </div>
                              </div>

                              {/* LEVEL 3: Observable Skills */}
                              <div>
                                <h5 className="section-heading-sm">
                                  Observable Skills
                                </h5>
                                <ul className="skill-list">
                                  {roleComp.observable_skills.map((skill, skillIdx) => {
                                    const words = skill.text.split(' ');
                                    const verb = words[0];
                                    const rest = words.slice(1).join(' ');
                                    
                                    return (
                                      <li
                                        key={skillIdx}
                                        className="skill-item"
                                        data-testid={`skill-${domainIdx}-${rcIdx}-${skillIdx}`}
                                      >
                                        <span className="skill-number"></span>
                                        <div className="skill-text">
                                          <span className="skill-verb">{verb}</span> {rest}
                                        </div>
                                      </li>
                                    );
                                  })}
                                </ul>
                              </div>

                              {/* Divider between role competencies */}
                              {rcIdx < domain.role_competencies.length - 1 && (
                                <div className="h-px bg-slate-200 my-6"></div>
                              )}
                            </div>
                          ))}

                          {/* AI-Enhanced Skill for Domain */}
                          <div className="ai-skill-badge" data-testid={`ai-skill-${domainIdx}`}>
                            <div className="ai-skill-label">
                              <Cpu className="w-4 h-4" />
                              AI-Enhanced Capability for {domain.company_value}
                            </div>
                            <p className="ai-skill-text">{domain.ai_enhanced_skill}</p>
                          </div>
                        </div>
                      </motion.div>

                      {/* Gold Divider between domains */}
                      {domainIdx < results.competency_domains.length - 1 && (
                        <div className="gold-divider"></div>
                      )}
                    </div>
                  ))}
                </div>

                {/* AI Disclaimer */}
                <div className="mt-10 mb-2 bg-amber-50 border-l-4 border-[#C9A84C] rounded-r-lg p-4" data-testid="ai-disclaimer">
                  <p className="text-xs text-slate-700 leading-relaxed">
                    <strong className="text-[#0F1E35]">Note:</strong> AI-generated content may contain inaccuracies. Please review all competencies with a qualified HR professional before formal use in hiring, performance management, or development processes. RoleIQ is a decision-support tool — not a substitute for professional HR judgement.
                  </p>
                </div>

                {/* JD Output */}
                {generatedJD && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-12"
                  >
                    <div className="gold-divider mb-8"></div>
                    
                    <div className="jd-output-card">
                      <div className="flex justify-between items-start mb-6">
                        <div>
                          <h2 className="text-2xl playfair font-bold text-[#0F1E35] mb-2">
                            Generated Job Description
                          </h2>
                          <p className="text-sm text-slate-500">
                            <Edit3 className="w-4 h-4 inline mr-1" />
                            Yellow highlighted fields are editable
                          </p>
                        </div>
                        
                        <div className="flex gap-2">
                          <button
                            onClick={handleCopyJD}
                            className="px-4 py-2 border-2 border-[#C9A84C] text-[#0F1E35] rounded-lg font-semibold text-sm transition-all flex items-center gap-2 hover:bg-[#C9A84C]/10"
                            data-testid="copy-jd-button"
                          >
                            {copied ? (
                              <>
                                <Check className="w-4 h-4" />
                                Copied!
                              </>
                            ) : (
                              <>
                                <Copy className="w-4 h-4" />
                                Copy
                              </>
                            )}
                          </button>
                          
                          <button
                            onClick={handleDownloadJDWord}
                            className="px-4 py-2 bg-[#0F1E35] text-[#C9A84C] rounded-lg font-semibold text-sm transition-all flex items-center gap-2 hover:bg-[#1a2d4a]"
                            data-testid="download-jd-word-button"
                          >
                            <Download className="w-4 h-4" />
                            Word
                          </button>
                        </div>
                      </div>
                      
                      <div className="jd-preview">
                        {generatedJD.split('\n').map((line, idx) => {
                          const isEditable = line.includes('[EDITABLE]');
                          const displayLine = line.replace(/\[EDITABLE\]/g, '');
                          
                          if (!displayLine.trim()) return <br key={idx} />;
                          
                          if (displayLine.includes('— JOB DESCRIPTION')) {
                            return (
                              <h1 key={idx} className="text-2xl playfair font-bold text-[#0F1E35] text-center mb-6 mt-4">
                                {displayLine}
                              </h1>
                            );
                          }
                          
                          if (displayLine.startsWith('About ') || 
                              displayLine === 'Role Summary:' || 
                              displayLine === 'Key Responsibilities:' ||
                              displayLine === 'Required Competencies (SKA Framework):' ||
                              displayLine === 'Qualifications & Experience:' ||
                              displayLine === 'What We Offer:') {
                            return (
                              <h2 key={idx} className="text-lg font-semibold text-[#C9A84C] mt-6 mb-3">
                                {displayLine}
                              </h2>
                            );
                          }
                          
                          return (
                            <p key={idx} className={`text-slate-700 leading-relaxed mb-2 ${isEditable ? 'editable-field' : ''}`}>
                              {displayLine}
                            </p>
                          );
                        })}
                      </div>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            </AnimatePresence>
          )}
        </div>
      </div>
    </div>
  );
}

export default GeneratePage;
